// store/slices/userSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { AxiosError } from 'axios'
import UserService from '@/services/users';
import localconfig from '@/config/local-config.json';

export interface User {
  id: string;
  name: string;
  firstname?: string;
  lastname?: string;
  email: string;
  superadmin: boolean;
  role?: string;
  cdmContactId?: string;
  cdmCompanyData?: any[];
  primaryId?: string;
}

interface UserState {
  data: User | null;
  loading: boolean;
  error: string | null;
  fetched: boolean;
  status: 'idle' | 'loading' | 'succeeded' | 'failed'
  errorCode?: number;
  errorDetails?: unknown;

}

const initialState: UserState = {
  data: null,
  loading: false,
  error: null,
  fetched: false,
  status: 'idle',
  errorCode: undefined,
  errorDetails: undefined,

};

type ThunkArg = {
  idTokenProvider: () => Promise<any>;
  accessTokenProvider: () => Promise<string>;
};

type RejectedPayload = {
  message: string;
  status?: number;
  details?: unknown;
};


/**
 * Normalize Axios error to {message, status, details}.
 */
function toRejectedPayload(err: unknown): RejectedPayload {
  const fallback: RejectedPayload = { message: 'Unknown error' };
  if (!err || typeof err !== 'object') return fallback;

  const axiosErr = err as AxiosError<any>;
  // Axios error shape: response?.status, response?.data, message
  const message =
    axiosErr.response?.data?.message ||
    axiosErr.message ||
    'Request failed';

  // console.log('toRejectedPayload: extracted error message', axiosErr.toJSON?.());
  const status = axiosErr.response?.status;
  // const details = axiosErr.response?.data ?? axiosErr.toJSON?.() ?? undefined;
  const details = {
    name: axiosErr.name,
    code: axiosErr.code,
    message: axiosErr.message,
    status: axiosErr.response?.status,
    // config: axiosErr.config,
    responseData: axiosErr.response?.data,
  };
  
  return { message, status, details };
}

// Async thunk for fetching user data
export const fetchUser = createAsyncThunk<User, ThunkArg,{ rejectValue: RejectedPayload }>
('user/fetchUser', async ({ idTokenProvider, accessTokenProvider }, { rejectWithValue }) => {
    try {
      console.log('fetchUserInfo: starting fetch');
      const idToken = await idTokenProvider();
      // console.log('fetchUserInfo: obtained ID token', idToken.__raw);
      const accessToken = await accessTokenProvider();
      
      const idTokenValue = import.meta.env.DEV ? localconfig.idToken : idToken.__raw;
      const accessTokenValue = import.meta.env.DEV ? localconfig.accessToken : accessToken;

      const userService = new UserService(idTokenValue);
      const userDetails = await userService.getDetails();
      console.log('fetchUserInfo: fetched user details', userDetails);
      
      // return userDetails;
      return {
        id: userDetails.id,
        email: userDetails.email,
        superadmin: userDetails.is_superadmin,
        cdmContactId: userDetails.cdmContactId,
        cdmCompanyData: userDetails.cdm_company_data,
        firstname: userDetails.first_name,
        lastname: userDetails.last_name,
        role: userDetails.user_role
      } as User;
    } catch (err) {
      return rejectWithValue(toRejectedPayload(err));
    }
    
  },
  {
    // condition to prevent duplicate requests
    condition: (_arg, { getState }) => {
      const state = getState() as { user: UserState };
           return state.user.status === 'idle' && !state.user.fetched;
    },
  }

);

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    clearUser: (state) => {
      state.loading = false;
      state.data = null;
      state.error = null;
      state.fetched = false; // Reset fetched flag
      state.status = 'idle';
      state.errorCode = undefined;
      state.errorDetails = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUser.pending, (state) => {
        state.status = 'loading';
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        const payloadData = action.payload;
        console.log('userSlice: fetched user', payloadData);
        state.loading = false;
        state.data = payloadData as User;
        state.fetched = true; // Mark as fetched
        state.status = 'succeeded';
      })
      .addCase(fetchUser.rejected, (state, action) => {
        state.status = 'failed';
        state.loading = false;
        // state.error = action.error.message || 'Failed to fetch user';
        state.fetched = true; // Still mark as fetched to prevent retries

        const payload = action.payload as RejectedPayload | undefined;
        if (payload) {
          state.error = payload.message;
          state.errorCode = payload.status || 99;
          state.errorDetails = payload.details;
        } else {
          // Fallback to RTK's generic error
          state.error = action.error.message || 'Failed to fetch user';
        }

        // Optional: special handling for 401/403
        // if (state.errorCode === 401 || state.errorCode === 403) {
        //   // e.g., dispatch(logout) or mark a flag to trigger re-auth UI
        // }

      });
  },
});

export const { clearUser } = userSlice.actions;
export default userSlice.reducer;